/**
 * toPaymentsView — pure `PaymentProjectionDto[] → PaymentView[]` mapper
 * (SCRUM-338).
 *
 * The `PaymentsTab` feature never consumes the raw payment DTOs: it consumes a
 * `PaymentView[]` computed here. Keeping the projection PURE (no hooks, no I/O,
 * no wall-clock read) makes it unit-testable in isolation and keeps the feature
 * a thin renderer — the exact posture of `to-ledger-view.ts` (SCRUM-336).
 *
 * Responsibilities (adversarial-TDD contract):
 *   - **Sort** by `occurredAt` DESC (newest first) — a stable, non-mutating sort
 *     over a copy of the input.
 *   - **Label maps** are EXHAUSTIVE, keyed on the CONTRACT union
 *     (`Record<PaymentProjectionDto['state'], string>` etc.). A new enum member
 *     on the wire fails typecheck here (compile-time drift protection) — the
 *     "keying the map on the union" idiom the plan calls out, no runtime gate.
 *     Precedent: these are DISPLAY labels for ONE consumer (ADR-first; promote to
 *     `@artemis/validators` + a runtime drift-gate only at a 2nd consumer, YAGNI).
 *   - **Direction** derived from `kind` (charge / refund) — carried as TEXT so
 *     the tab can convey it without color (WCAG 1.4.1).
 *
 * MONEY & TIME boundaries (CLAUDE.md rule 6 / FE rules §5/§6): the mapper NEVER
 * divides, multiplies, sums, or formats. `amountCents` (a `Cents` integer) and
 * `occurredAt` (an ISO 8601 string) pass through UNTOUCHED — `formatCents` /
 * `intl.formatDate` run at the display boundary in the feature. `gatewayRef` /
 * `processorName` are passed through as `string | null` (the renderer maps
 * `null → "—"`, never here).
 */

import type { PaymentProjectionDto } from './types.js';

/** One payment-log row, projected for display. */
export interface PaymentView {
  /** PaymentId (UUID v7) — the stable React key. */
  readonly paymentId: string;
  /** `Cents` integer — passthrough (never divided / formatted here). */
  readonly amountCents: number;
  /** ISO 8601 string — passthrough (formatted via `intl.formatDate` at the boundary). */
  readonly occurredAt: string;
  /** Localized method label (from `baseSystemType`). */
  readonly methodLabel: string;
  /** Localized FSM status label (from `state`). */
  readonly statusLabel: string;
  /** Localized direction label (from `kind`) — carried as TEXT, never color. */
  readonly directionLabel: string;
  /** Processor name; `null` → the renderer shows "—". */
  readonly processorName: string | null;
  /** Gateway reference; `null` → the renderer shows "—". */
  readonly gatewayRef: string | null;
}

/**
 * `baseSystemType` → method label. EXHAUSTIVE over the contract union — a new
 * member fails typecheck. Labels are deliberately kept clear of the direction /
 * status vocabulary (charge / refund / …) so each column reads as ONE
 * unambiguous concept for assistive tech (no cross-column word overlap).
 */
const METHOD_LABELS: Record<PaymentProjectionDto['baseSystemType'], string> = {
  CC: 'Card',
  ACH: 'Bank transfer',
  Cash: 'Cash',
  Adjustment: 'Adjustment',
  Owner: 'Owner account',
  Channel: 'Channel',
};

/**
 * `state` → status label. EXHAUSTIVE over the 8-value FSM union — a new member
 * fails typecheck at THIS declaration (the drift guard). Copy mirrors the
 * entity-change Payment catalog.
 */
const STATUS_LABELS: Record<PaymentProjectionDto['state'], string> = {
  Intended: 'Intended',
  Authorized: 'Authorized',
  Captured: 'Captured',
  Settled: 'Settled',
  Voided: 'Voided',
  Failed: 'Failed',
  Refunded: 'Refunded',
  PartiallyRefunded: 'Partially refunded',
};

/**
 * `kind` → direction label. EXHAUSTIVE over the contract union — a new member
 * fails typecheck at THIS declaration. Distinct labels so a refund reads
 * differently from a charge WITHOUT relying on color (WCAG 1.4.1). A refund is
 * labelled "Reversal" — a money-out reversal of a prior charge — kept verbally
 * distinct from the FSM `state` column's "Refunded" so the two columns never
 * blur into one another.
 */
const DIRECTION_LABELS: Record<PaymentProjectionDto['kind'], string> = {
  charge: 'Charge',
  refund: 'Reversal',
};

/**
 * Read a total label map, falling back to the raw wire value when a member is
 * somehow unmapped. The `Record<Union, string>` types above make every union
 * member mapped at declaration, so the fallback is unreachable for
 * in-contract data — it exists only to (a) satisfy `noUncheckedIndexedAccess`
 * (which widens an indexed read to `string | undefined`) WITHOUT a silent-empty
 * default, and (b) render an out-of-contract value honestly (its raw string)
 * rather than blank, per the plan's honest-raw-render doctrine.
 */
function labelFor(map: Record<string, string>, key: string): string {
  return map[key] ?? key;
}

/** Compare two ISO 8601 timestamps DESC (newest first). Lexicographic order on
 *  a well-formed ISO string equals chronological order — no Date parse needed. */
function byOccurredAtDesc(a: PaymentProjectionDto, b: PaymentProjectionDto): number {
  if (a.occurredAt < b.occurredAt) return 1;
  if (a.occurredAt > b.occurredAt) return -1;
  return 0;
}

export function toPaymentsView(payments: PaymentProjectionDto[]): PaymentView[] {
  // Sort a COPY (never mutate the caller's array — the hook's cached data).
  return [...payments].sort(byOccurredAtDesc).map(
    (p: PaymentProjectionDto): PaymentView => ({
      paymentId: p.paymentId,
      amountCents: p.amountCents,
      occurredAt: p.occurredAt,
      methodLabel: labelFor(METHOD_LABELS, p.baseSystemType),
      statusLabel: labelFor(STATUS_LABELS, p.state),
      directionLabel: labelFor(DIRECTION_LABELS, p.kind),
      processorName: p.processorName ?? null,
      gatewayRef: p.gatewayRef ?? null,
    }),
  );
}
